import type {
  LessonBlockCouponRestoration,
  LessonBlockDetail,
  LessonBlockSource,
  LessonCreditTransaction,
} from "./staff.js";
import type { AutomaticSettlementPolicy } from "./lessonBlockSaleModel.js";

export type CreditCorrectionKind =
  | "legacy_migration"
  | "legacy_adjustment"
  | "goodwill_adjustment"
  | "paid_balance_adjustment"
  | "goodwill"
  | "credit_restore"
  | "coupon_restore"
  | "expiry"
  | "refund";

export const CREDIT_CORRECTION_LABELS: Record<CreditCorrectionKind, string> = {
  legacy_migration: "Legacy migration",
  legacy_adjustment: "Legacy migration adjustment",
  goodwill_adjustment: "Goodwill credit adjustment",
  paid_balance_adjustment: "Correct credit balance",
  goodwill: "Goodwill / free credit",
  credit_restore: "Credit restoration",
  coupon_restore: "Coupon-use restoration",
  expiry: "Expiry extension",
  refund: "External refund",
};

/**
 * The exceptional migration/goodwill/refund/restore/expiry console remains an
 * explicit financial-management surface. Ordinary add/close lesson-block
 * operations are inherent own scope and are exposed through the tutor tools,
 * not by broadening this console.
 */
export function canUseCreditCorrectionConsole(
  capabilities: readonly string[],
  owner: boolean,
  _providerId: number | null = null,
): boolean {
  if (owner) return true;
  const held = new Set(capabilities);
  return held.has("lesson_blocks.manage.all") || held.has("lesson_blocks.manage.own_students");
}

export interface CorrectionAttempt {
  fingerprint: string;
  key: string;
}

export interface GoodwillCreditInput {
  portal_student_id: string;
  provider_id: number;
  block_service_id: number;
  quantity: number;
  reason: string;
  automatic_settlement_policy: AutomaticSettlementPolicy;
}

export function goodwillCreditInput(input: GoodwillCreditInput): GoodwillCreditInput {
  return input;
}

export function automaticSettlementPolicyLabel(
  policy: AutomaticSettlementPolicy | null | undefined,
): string | null {
  if (policy === "settle_outstanding_first") return "Outstanding lessons first";
  if (policy === "no_automatic_backdating") return "No automatic backdating";
  return null;
}

/**
 * One UUID belongs to one logical correction, not to one press of a button.
 * Repeating an unchanged request after an ambiguous response therefore keeps
 * its key; editing the proposed operation starts a genuinely new attempt.
 */
export function correctionAttempt(
  previous: CorrectionAttempt | null,
  kind: CreditCorrectionKind,
  input: unknown,
  createKey: () => string,
): CorrectionAttempt {
  const fingerprint = `${kind}:${JSON.stringify(input)}`;
  return previous?.fingerprint === fingerprint
    ? previous
    : { fingerprint, key: createKey() };
}

export function sourceLabel(source: LessonBlockSource): string {
  switch (source) {
    case "legacy_migration":
      return "Legacy migration / carried forward";
    case "goodwill":
      return "Goodwill / free credit";
    case "amelia":
      return "Paid block · booking system";
    case "staff":
      return "Paid block · recorded by staff";
    case "transfer":
      return "Transferred entitlement";
    case "paid_cancellation":
      return "Paid cancellation credit";
  }
}

export function transactionLabel(
  transaction: LessonCreditTransaction,
  source: LessonBlockSource,
): string {
  if (
    transaction.transaction_type === "grant" &&
    transaction.related_transaction_id !== null &&
    source === "legacy_migration"
  ) {
    return CREDIT_CORRECTION_LABELS.legacy_adjustment;
  }
  if (
    transaction.transaction_type === "grant" &&
    transaction.related_transaction_id !== null &&
    source === "goodwill"
  ) {
    return CREDIT_CORRECTION_LABELS.goodwill_adjustment;
  }
  if (
    transaction.transaction_type === "grant" &&
    source === "legacy_migration"
  ) {
    return CREDIT_CORRECTION_LABELS.legacy_migration;
  }
  if (transaction.transaction_type === "grant" && source === "goodwill") {
    return CREDIT_CORRECTION_LABELS.goodwill;
  }
  if (transaction.transaction_type === "grant" && source === "paid_cancellation") {
    return "Rebooking credit issued after paid lesson cancellation";
  }
  if (
    transaction.transaction_type === "adjustment" &&
    source === "legacy_migration"
  ) {
    return CREDIT_CORRECTION_LABELS.legacy_adjustment;
  }
  if (transaction.transaction_type === "adjustment" && source === "goodwill") {
    return CREDIT_CORRECTION_LABELS.goodwill_adjustment;
  }
  if (
    transaction.transaction_type === "adjustment" &&
    (source === "amelia" || source === "staff")
  ) {
    return CREDIT_CORRECTION_LABELS.paid_balance_adjustment;
  }
  if (transaction.transaction_type === "restore") {
    return CREDIT_CORRECTION_LABELS.credit_restore;
  }
  if (transaction.transaction_type === "use") return "Lesson credit used";
  if (transaction.transaction_type === "reversal") return "Credit reversal";
  if (transaction.transaction_type === "adjustment") return "Credit adjustment";
  return "Credit grant";
}

/** Specific consumed transactions that have not already been restored. */
export function restorableUses(
  block: LessonBlockDetail,
): LessonCreditTransaction[] {
  const restored = new Set(
    block.transactions
      .filter((transaction) => transaction.transaction_type === "restore")
      .map((transaction) => transaction.related_transaction_id)
      .filter((id): id is string => id !== null),
  );
  return block.transactions.filter(
    (transaction) =>
      transaction.transaction_type === "use" &&
      transaction.amelia_appointment_id !== null &&
      !restored.has(transaction.id),
  );
}

/** Only durable reconciliation rows explicitly marked for staff attention. */
export function couponReviewItems(
  block: LessonBlockDetail,
): LessonBlockCouponRestoration[] {
  return (block.coupon?.restorations ?? []).filter(
    (restoration) => restoration.status === "requires_attention",
  );
}
